import React, { useEffect, useState } from 'react';
import { Analytics } from '@vercel/analytics/react';
import DynamicVoronoi from './DynamicVoronoi';
import ProjectDetail from './ProjectDetail';
import projects from './projects';
import './App.css';

const Arrow = () => <span aria-hidden="true">↗</span>;

const career = [
  {
    dates: '2025 — Now',
    role: 'Global Solution Architect',
    company: 'CEA · Le Barp',
    copy: 'Design authority for a PLM, MES and ERP transformation spanning systems engineering, design, simulation, manufacturing engineering, production and data migration.',
    tags: ['3DEXPERIENCE', 'Apriso', 'Data migration'],
  },
  {
    dates: '2022 — 2024',
    role: 'Senior Business Analyst',
    company: 'Dassault Falcon Jet · Little Rock',
    copy: 'Connected Engineering to Manufacturing Engineering, qualified the EBOM-to-MBOM bridge and made product data reliable for stock and procurement systems.',
    tags: ['PLM', 'EBOM / MBOM', 'International'],
  },
  {
    dates: '2019 — 2022',
    role: 'Head of Optronic Sensor Flight Tests',
    company: 'DGA Flight Test Center · Cazaux',
    copy: 'Led a department of 20 engineers and specialists, balancing workload, capacity and test-means budgets while directing airborne development and qualification campaigns for advanced optronic sensors.',
    tags: ['Flight testing', 'Optronics', 'Team leadership'],
  },
  {
    dates: '2015 — 2019',
    role: 'MRO Supply Chain Manager',
    company: 'French aeronautical maintenance center · Cuers',
    copy: 'Built and led a 20-person supply-chain team for an aircraft MRO line: shortages, internal manufacturing, external procurement, SAP ECC and line-side delivery.',
    tags: ['SAP ECC', 'Production', 'Supply chain'],
  },
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const analyticsEnabled = !['localhost', '127.0.0.1'].includes(window.location.hostname);
  const experienceYears = new Date().getFullYear() - 2014;
  const projectSlug = window.location.pathname.match(/^\/projects\/([^/]+)\/?$/)?.[1];
  const selectedProject = projects.find((project) => project.slug === projectSlug);

  useEffect(() => {
    const closeMenu = () => setMenuOpen(false);
    document.title = selectedProject
      ? `${selectedProject.name} — Antoine Marchal`
      : 'Antoine Marchal — Industrial Systems Architect';
    window.addEventListener('resize', closeMenu);
    return () => window.removeEventListener('resize', closeMenu);
  }, [selectedProject]);

  if (selectedProject) {
    return <ProjectDetail project={selectedProject} analyticsEnabled={analyticsEnabled} />;
  }

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Antoine Marchal, home">
          <span className="brand-copy">Antoine Marchal<small>Industrial Systems Architect</small></span>
        </a>
        <button className="menu-toggle" type="button" aria-expanded={menuOpen} aria-controls="primary-nav" onClick={() => setMenuOpen(!menuOpen)}>
          <span /><span />
          <span className="sr-only">Toggle navigation</span>
        </button>
        <nav id="primary-nav" className={menuOpen ? 'nav-links is-open' : 'nav-links'} aria-label="Primary navigation">
          <a href="#work" onClick={() => setMenuOpen(false)}>Work</a>
          <a href="#experience" onClick={() => setMenuOpen(false)}>Experience</a>
          <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
          <a href="#products" onClick={() => setMenuOpen(false)}>Products</a>
          <a className="nav-cta" href="mailto:antoine.marchal@pm.me">Let’s talk <Arrow /></a>
        </nav>
      </header>

      <main>
        <section className="hero" id="top">
          <DynamicVoronoi className="background-voronoi" />
          <div className="hero-glow" aria-hidden="true" />
          <div className="hero-content">
            <p className="kicker"><span /> Aerospace · Production · Digital continuity</p>
            <h1>I connect the factory, its data and the systems <em>that run it.</em></h1>
            <p className="hero-lede">
              Industrial systems architect and former aerospace operations manager. I bridge PLM, MES, ERP and supply-chain reality—from production needs to governed data and working software.
            </p>
            <div className="hero-actions">
              <a className="button button-primary" href="#work">Explore the work <span aria-hidden="true">↓</span></a>
              <details className="resume-menu">
                <summary className="button button-ghost">View résumé <span aria-hidden="true">⌄</span></summary>
                <div className="resume-options" aria-label="Choose résumé language">
                  <a href="/resume.pdf" target="_blank" rel="noreferrer"><strong>FR</strong><span>Version française</span><Arrow /></a>
                  <a href="/resume-en.pdf" target="_blank" rel="noreferrer"><strong>EN</strong><span>English version</span><Arrow /></a>
                </div>
              </details>
            </div>
          </div>
          <div className="hero-meta" aria-label="Key facts">
            <div><strong>{experienceYears}+</strong><span>Years since 2014</span></div>
          </div>
          <a className="scroll-cue" href="#work"><span>Scroll to explore</span><i aria-hidden="true" /></a>
        </section>

        <section className="work-section" id="work">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Software built from industrial needs</p>
              <h2>Tools for the hard parts of <span>digital continuity.</span></h2>
            </div>
            <p>I created them while solving recurring project problems: moving and cleaning data, making architecture understandable, governing execution and turning operational data into decisions.</p>
          </div>

          <div className="projects">
            {projects.map((project) => (
              <article className={project.featured ? 'project project-featured' : 'project'} key={project.name}>
                <div className="project-visual">
                  <div className="window-chrome"><span /><span /><span /><b>{project.name.toLowerCase().replaceAll(' ', '-')}.local</b></div>
                  <img src={project.image} alt={project.alt} loading="lazy" />
                  <span className="project-index">{project.number}</span>
                </div>
                <div className="project-copy">
                  <p className="project-eyebrow">{project.eyebrow}</p>
                  <h3>{project.name}</h3>
                  <p className="project-description">{project.description}</p>
                  <ul className="highlight-list">
                    {project.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
                  </ul>
                  <div className="tag-list">
                    {project.tags.map((tag) => <span key={tag}>{tag}</span>)}
                  </div>
                  <a className="text-link" href={`/projects/${project.slug}`}>View details <Arrow /></a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="experience-section" id="experience">
          <div className="experience-heading">
            <p className="eyebrow">Experience that spans the whole loop</p>
            <h2>From aircraft operations to industrial information systems.</h2>
            <p>My architecture decisions are grounded in production responsibility, supply-chain constraints and direct experience of aerospace programmes.</p>
          </div>
          <div className="career-list">
            {career.map((item) => (
              <article className="career-item" key={item.dates}>
                <time>{item.dates}</time>
                <div><p>{item.company}</p><h3>{item.role}</h3><span>{item.copy}</span></div>
                <ul>{item.tags.map((tag) => <li key={tag}>{tag}</li>)}</ul>
              </article>
            ))}
          </div>
        </section>

        <section className="about-section" id="about">
          <div className="about-intro">
            <p className="eyebrow">How I work</p>
            <h2>Architecture grounded on the shop floor.</h2>
          </div>
          <div className="about-copy">
            <p className="about-lead">I can speak with production teams about shortages and work orders, with engineering about product structures, and with IT about interfaces, performance and deployment.</p>
            <p>My path spans aircraft MRO production, supply-chain management, flight testing, Dassault Falcon Jet manufacturing transformation and global solution architecture. That gives me a systems-of-systems perspective: processes, data, applications, interfaces, infrastructure and people are designed together. When a missing tool blocks delivery, I build it.</p>
            <div className="principles">
              <div><span>01</span><strong>Map the system</strong><p>Make dependencies and decisions visible.</p></div>
              <div><span>02</span><strong>Protect data continuity</strong><p>From EBOM and MBOM to MES, ERP and reporting.</p></div>
              <div><span>03</span><strong>Design with operators</strong><p>Solutions that survive real production constraints.</p></div>
            </div>
          </div>
        </section>

        <section className="download-section" id="products">
          <div className="download-heading">
            <p className="eyebrow">Product portfolio</p>
            <h2>Proof that I turn architecture into working software.</h2>
            <p>Explore the projects, their architecture, and their public source code. Questions and reviews are welcome; please contact me before reusing a project.</p>
          </div>
          <div className="download-grid">
            {projects.map((project) => (
              <article className={project.featured ? 'download-card is-featured' : 'download-card'} key={project.name}>
                {project.featured && <span className="featured-label">Flagship</span>}
                <div className="download-number">{project.number}</div>
                <p>{project.eyebrow}</p>
                <h3>{project.name}</h3>
                <ul>{project.highlights.map((item) => <li key={item}>{item}</li>)}</ul>
                <a href={`/projects/${project.slug}`}>View details <Arrow /></a>
                <small>Public source · Feedback welcome</small>
              </article>
            ))}
          </div>
          <p className="download-note">Source code for all three projects is available on GitHub.</p>
        </section>

        <section className="contact-section">
          <p className="eyebrow">Have a complex system?</p>
          <h2>Let’s make it <span>understandable.</span></h2>
          <a className="button button-light" href="mailto:antoine.marchal@pm.me">Start a conversation <Arrow /></a>
        </section>
      </main>

      <footer>
        <div className="footer-brand"><strong>AM</strong><span>Antoine Marchal<br />Industrial Systems Architect</span></div>
        <div className="footer-links">
          <a href="https://www.linkedin.com/in/antoine-marchal-4823878a/" target="_blank" rel="noreferrer">LinkedIn <Arrow /></a>
          <a href="mailto:antoine.marchal@pm.me">Email <Arrow /></a>
        </div>
        <p>© {new Date().getFullYear()} Antoine Marchal</p>
      </footer>
      {analyticsEnabled && <Analytics />}
    </div>
  );
}

export default App;
